// Mesmas convenções de `financialProjection/disbursementCoverageApi.js`:
// base em SERVER_URL, Bearer do cookie, leitura tolerante (devolve null em falha
// HTTP/rede) e escrita que sempre devolve o status code para a tela distinguir
// 202 (regenerando) de 409 (sobreposição de grupo).
import Cookies from "universal-cookie";
import { normalizeDisbursementCoverage } from "./coverage";

const cookies = new Cookies();
const BASE = process.env.SERVER_URL || "";
const enc = encodeURIComponent;
const COMPETENCE = /^\d{4}-(0[1-9]|1[0-2])$/;

const authorization = () => {
  const { token } = cookies.get("userId") || {};
  return { Authorization: `Bearer ${token}` };
};

const jsonHeaders = () => ({
  ...authorization(),
  "Content-Type": "application/json",
});

const moduleUrl = (projectId, path, params) => {
  const query =
    params && [...params.keys()].length > 0 ? `?${params.toString()}` : "";
  return `${BASE}/project/${enc(projectId)}/disbursement${path}${query}`;
};

const readJson = async (url) => {
  try {
    const response = await fetch(url, { headers: authorization() });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
};

const writeJson = async (url, method, body) => {
  try {
    const response = await fetch(url, {
      method,
      headers: jsonHeaders(),
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    let payload = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }
    // O status HTTP é autoritativo: espalha o corpo primeiro e sobrescreve `code`
    // com `response.status` por último, pra um corpo malicioso/inconsistente
    // (ex.: `{ code: "x" }`) nunca mascarar o código real (ex.: 409).
    const base =
      payload && typeof payload === "object" && !Array.isArray(payload)
        ? payload
        : {};
    return { ...base, code: response.status };
  } catch {
    return { code: 0, errorCode: "network_error" };
  }
};

const missingScope = () => ({ code: 0, errorCode: "project_scope_required" });

/**
 * FE6 (ruling): a rota `GET .../disbursement/rules` não tem filtros de query no
 * back-end (ver BE plan Task 16) — qualquer `targetKind`/`source` recebido aqui é
 * ignorado de propósito; a filtragem é client-side, feita na lista (tarefa futura).
 */
export const fetchDisbursementRules = async (projectId) => {
  if (!projectId) return null;
  return readJson(moduleUrl(projectId, "/rules"));
};

export const saveDisbursementRule = async (
  projectId,
  { target, profile, description, source } = {},
) => {
  if (!projectId) return missingScope();
  return writeJson(moduleUrl(projectId, "/rules"), "POST", {
    target,
    profile,
    ...(description === undefined ? {} : { description }),
    ...(source === undefined ? {} : { source }),
  });
};

export const patchDisbursementRule = async (projectId, ruleId, patch = {}) => {
  if (!projectId || !ruleId) return missingScope();
  return writeJson(
    moduleUrl(projectId, `/rules/${enc(ruleId)}`),
    "PATCH",
    patch,
  );
};

/**
 * FE3 (ruling): `moveOnOverlap` é sempre encaminhado no corpo quando presente; uma
 * resposta 409 (`group_overlap`) expõe `conflictingGroup` e o `errorCode` pro
 * chamador — `writeJson` já devolve o corpo inteiro, então nada é engolido aqui.
 */
export const saveDisbursementGroup = async (
  projectId,
  { groupId, name, criterion, dynamic, moveOnOverlap } = {},
) => {
  if (!projectId) return missingScope();
  const body = {
    name,
    criterion,
    ...(dynamic === undefined ? {} : { dynamic }),
    ...(moveOnOverlap === undefined ? {} : { moveOnOverlap }),
  };
  return groupId
    ? writeJson(moduleUrl(projectId, `/groups/${enc(groupId)}`), "PATCH", body)
    : writeJson(moduleUrl(projectId, "/groups"), "POST", body);
};

export const updateDisbursementAdjustment = async (projectId, payload = {}) => {
  if (!projectId) return missingScope();
  return writeJson(moduleUrl(projectId, "/adjustments"), "POST", payload);
};

/**
 * FE1 (ruling): cada alvo devolve `hasRule = ruleId !== null` derivado — o BE não
 * manda esse campo. O mapeamento do tipo de UI "resource_group" para os valores de
 * query `erp_group`/`ampli_group` é responsabilidade do chamador
 * (`targetQueryLevel`); `level` é repassado como veio.
 *
 * `?level=` NUNCA é omitido: a rota o exige e responde 400 `invalid_target_level`
 * sem ele (nem `"all"` existe no vocabulário do back-end). Sem nível a chamada nem
 * sai — devolve `null` como qualquer outra leitura que falhou, em vez de gastar um
 * 400 que o chamador leria como "esta obra não tem alvos".
 */
export const fetchDisbursementTargets = async (
  projectId,
  { level, search, page, pageSize, classification, limit } = {},
) => {
  if (!projectId) return null;
  if (!level || level === "all") return null;
  const params = new URLSearchParams();
  params.set("level", level);
  if (search) params.set("search", search);
  if (page !== undefined) params.set("page", page);
  if (pageSize !== undefined) params.set("pageSize", pageSize);
  if (classification !== undefined)
    params.set("classification", classification);
  // Non-catalogue mode only (no `page`/`pageSize`): the route caps at 500 and rejects a
  // catalogue query (page/pageSize present) for any level other than `resource`, so a
  // node-level browse (the EAP catalog) never sends those two.
  if (limit !== undefined && page === undefined && pageSize === undefined) {
    params.set("limit", limit);
  }
  const body = await readJson(moduleUrl(projectId, "/targets", params));
  if (!body || !Array.isArray(body.items)) return body;
  return {
    ...body,
    items: body.items.map((item) => ({
      ...item,
      hasRule: item.ruleId !== null && item.ruleId !== undefined,
    })),
  };
};

export const fetchDisbursementMonth = async (
  projectId,
  competence,
  { wbs } = {},
) => {
  if (!projectId || !COMPETENCE.test(String(competence ?? ""))) return null;
  const params = new URLSearchParams();
  if (wbs) params.set("wbs", wbs);
  return readJson(moduleUrl(projectId, `/month/${competence}`, params));
};

export const regenerateAmpliDisbursement = async (projectId) => {
  if (!projectId) return missingScope();
  return writeJson(moduleUrl(projectId, "/regenerate"), "POST");
};

const rowIdOf = (item) => {
  if (item.kind === "rule") return `rule:${item.ruleId}`;
  if (item.kind === "group") return `group:${item.groupId}`;
  if (item.kind === "adjustment") return `adjustment:${item.entryId}`;
  return `erp_group_without_rule:${item.key}`;
};

/**
 * O número que a tabela lê, qualquer que seja o `kind`: linhas de `rule`/`group`
 * carregam `coveredCents` (`getDisbursementRules.js`), as de `erpGroupsWithoutRule`
 * (target view) carregam `plannedCents`, e as de `adjustment` carregam `appliedCents`.
 *
 * `null` é PRESERVADO, nunca virado 0. O back-end distingue as duas coisas de
 * propósito — `coveredCents: null` é "não deu para computar a cobertura deste alvo"
 * (composição degradada, alvo fora do picker) e `appliedCents: null` é "nenhuma curva
 * foi gerada para este ajuste ainda". Um `0` no lugar leria como veredito do motor
 * ("nada foi coberto"/"o ajuste foi rejeitado"), que é afirmação bem diferente.
 */
const centsOf = (item) => {
  const value = item?.coveredCents ?? item?.plannedCents ?? item?.appliedCents;
  return Number.isFinite(value) ? value : null;
};

const countOf = (value) =>
  Number.isSafeInteger(value) && value >= 0 ? value : null;

const enrich = (item) => {
  const cents = centsOf(item);
  return {
    ...item,
    ...(item.kind === "rule"
      ? { hasRule: item.ruleId !== null && item.ruleId !== undefined }
      : {}),
    ...(item.kind === "group"
      ? { memberCount: countOf(item.memberCount) }
      : { itemCount: countOf(item.itemCount) }),
    coveredCents: cents,
    rowId: rowIdOf(item),
  };
};

/** Sem número (`null`) vai pro FIM da lista, não pro topo com os zeros. */
const compareCents = (left, right) => {
  if (left.coveredCents === right.coveredCents) return 0;
  if (left.coveredCents === null) return 1;
  if (right.coveredCents === null) return -1;
  return right.coveredCents - left.coveredCents;
};

/**
 * Uma lista só para a página, aceitando as duas formas possíveis da rota:
 * `{ items }` já unificada ou `{ rules, groups, erpGroupsWithoutRule, adjustments }`.
 * Ordena por previsto coberto (decrescente) — é a ordem em que o cliente cobre a obra.
 */
export const normalizeDisbursementList = (payload) => {
  if (!payload || typeof payload !== "object") {
    return { items: [], coverage: null, state: null };
  }
  const fromSingleList = Array.isArray(payload.items)
    ? payload.items.map((item) => ({ ...item, kind: item.kind ?? "rule" }))
    : [
        ...(Array.isArray(payload.groups) ? payload.groups : []).map(
          (item) => ({ ...item, kind: "group" }),
        ),
        ...(Array.isArray(payload.rules) ? payload.rules : []).map((item) => ({
          ...item,
          kind: "rule",
        })),
        ...(Array.isArray(payload.erpGroupsWithoutRule)
          ? payload.erpGroupsWithoutRule
          : []
        ).map((item) => ({ ...item, kind: "erp_group_without_rule" })),
        ...(Array.isArray(payload.adjustments) ? payload.adjustments : []).map(
          (item) => ({ ...item, kind: "adjustment" }),
        ),
      ];
  const items = fromSingleList
    .map(enrich)
    .sort(
      (left, right) =>
        compareCents(left, right) || left.rowId.localeCompare(right.rowId),
    );
  return {
    items,
    // `coverage` normalmente vem no topo do payload, mas a rota `GET /rules`
    // aninha em `state.coverage` (`{ state: { status, reason, generatedAt,
    // revision, coverage }, rules, groups, adjustments, erpGroupsWithoutRule }`).
    coverage: normalizeDisbursementCoverage(
      payload.coverage ?? payload.state?.coverage,
    ),
    resourceSummary: payload.resourceSummary ?? null,
    state: payload.state ?? null,
  };
};
